// src/features/dashboard/components/EmployeeLoggedHoursReport/WeeklyHoursChart.tsx

import { useState } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  Legend,
} from 'recharts';

import {
  WtdLoggedResponse,
} from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  data?: WtdLoggedResponse[];
}

interface ChartDataItem {
  day: string;
  logged: number;
  target: number;
  loggedHours: string;
}

/* ============================================================================
 * TARGET CONFIGURATION
 * ========================================================================== */

/*
 * API targetHours is the WEEKLY target.
 *
 * Example API:
 *
 * targetHours: 45
 *
 * This chart is day-by-day, therefore:
 *
 * 45 / 5 working days = 9 hours per day
 */

const WEEKLY_TARGET_HOURS = 45;
const WORKING_DAYS = 5;

const DAILY_TARGET_HOURS =
  WEEKLY_TARGET_HOURS / WORKING_DAYS;

/* ============================================================================
 * CONVERT HH:mm:ss TO DECIMAL HOURS
 * ========================================================================== */

const convertToHours = (
  value?: string | null,
): number => {
  if (!value) {
    return 0;
  }

  const parts = String(value)
    .split(':')
    .map(Number);

  const hours =
    Number.isFinite(parts[0])
      ? parts[0]
      : 0;

  const minutes =
    Number.isFinite(parts[1])
      ? parts[1]
      : 0;

  const seconds =
    Number.isFinite(parts[2])
      ? parts[2]
      : 0;

  return (
    hours +
    minutes / 60 +
    seconds / 3600
  );
};

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

const WeeklyHoursChart = ({
  data = [],
}: Props) => {
  const [
    activeLegend,
    setActiveLegend,
  ] = useState<
    'logged' | 'target' | null
  >(null);

  /* ==========================================================================
   * PREPARE CHART DATA
   * ========================================================================== */

  const chartData: ChartDataItem[] =
    Array.isArray(data)
      ? data.map((item) => {
          /*
           * ---------------------------------------------------------------
           * Logged hours
           *
           * API:
           * 05:36:00
           *
           * Chart:
           * 5.60
           * ---------------------------------------------------------------
           */

          const hours =
            convertToHours(
              item.loggedHours,
            );

          /*
           * ---------------------------------------------------------------
           * BUILD DAY LABEL
           *
           * Example:
           *
           * Monday + 2026-06-29
           *
           *       ↓
           *
           * Mon 29
           *
           * IMPORTANT:
           * There is now a SPACE between
           * weekday and date.
           * ---------------------------------------------------------------
           */

          const workDate =
            item.workDate
              ? item.workDate.split('T')[0]
              : '';

          const datePart =
            workDate
              ? workDate.split('-')[2]
              : '';

          const apiDayName =
            item.dayLabel
              ? item.dayLabel.split(' ')[0]
              : '';

          const shortDay =
            apiDayName.substring(0, 3);

          /*
           * IMPORTANT:
           *
           * Before:
           *
           * Mon29
           *
           * Now:
           *
           * Mon 29
           *
           * This gives:
           *
           * Mon 10
           * Tue 11
           * Wed 12
           * Thu 13
           * Fri 14
           */

          const chartDay =
            datePart
              ? `${shortDay} ${datePart}`
              : shortDay;

          /*
           * ---------------------------------------------------------------
           * IMPORTANT
           *
           * Do NOT use:
           *
           * target: Number(item.targetHours)
           *
           * because API returns:
           *
           * targetHours: 45
           *
           * 45 is weekly target.
           *
           * This chart is daily, therefore use:
           *
           * 45 / 5 = 9
           * ---------------------------------------------------------------
           */

          return {
            day: chartDay,

            logged:
              Number(
                hours.toFixed(2),
              ),

            target:
              DAILY_TARGET_HOURS,

            loggedHours:
              item.loggedHours ||
              '00:00:00',
          };
        })
      : [];

  /* ==========================================================================
   * TARGET
   * ========================================================================== */

  /*
   * Always use 9h for daily target.
   *
   * Do not read item.targetHours here because
   * API targetHours is 45 weekly hours.
   */

  const targetHours =
    DAILY_TARGET_HOURS;

  /* ==========================================================================
   * TOOLTIP
   * ========================================================================== */

  const CustomTooltip = ({
    active,
    payload,
  }: any) => {
    if (
      !active ||
      !payload?.length
    ) {
      return null;
    }

    const item =
      payload[0]?.payload;

    const dataKey =
      payload[0]?.dataKey;

    if (
      !item ||
      !dataKey
    ) {
      return null;
    }

    const isLogged =
      dataKey === 'logged';

    return (
      <div
        className="
          min-w-[140px]
          overflow-hidden
          rounded-[8px]
          border
          border-[#D0D5DD]
          bg-white
          shadow-lg
        "
      >
        {/* ===============================================================
            DAY
        ================================================================ */}

        <div
          className="
            border-b
            border-[#EAECF0]
            bg-[#F8F9FB]
            px-3
            py-2
          "
        >
          <span
            className="
              text-[12px]
              font-medium
              text-[#344054]
            "
          >
            {item.day}
          </span>
        </div>

        {/* ===============================================================
            VALUE
        ================================================================ */}

        <div
          className="
            flex
            items-center
            gap-2
            px-3
            py-3
          "
        >
          <span
            className="
              h-3
              w-3
              rounded-full
            "
            style={{
              backgroundColor:
                isLogged
                  ? '#244E78'
                  : '#E5E7EB',
            }}
          />

          <span
            className="
              text-[13px]
              text-[#344054]
            "
          >
            {isLogged
              ? 'Logged:'
              : 'Target:'}
          </span>

          <span
            className="
              ml-auto
              text-[13px]
              font-semibold
              text-[#111827]
            "
          >
            {isLogged
              ? item.loggedHours
              : `${targetHours}h`}
          </span>
        </div>
      </div>
    );
  };

  /* ==========================================================================
   * DYNAMIC Y AXIS
   * ========================================================================== */

  const allYValues =
    chartData.flatMap(
      (item) => [
        item.logged,
        item.target,
      ],
    );

  const minValue =
    Math.min(
      ...allYValues,
      0,
    );

  const maxValue =
    Math.max(
      ...allYValues,
      DAILY_TARGET_HOURS,
    );

  const yMin =
    Math.max(
      0,
      Math.floor(
        (minValue - 1) * 2,
      ) / 2,
    );

  const yMax =
    Math.ceil(
      (maxValue + 1) * 2,
    ) / 2;

  /* ==========================================================================
   * GENERATE 0.5 HOUR TICKS
   * ========================================================================== */

  const yTicks: number[] = [];

  for (
    let value = yMin;
    value <= yMax;
    value += 0.5
  ) {
    yTicks.push(
      Number(
        value.toFixed(1),
      ),
    );
  }

  /* ==========================================================================
   * EMPTY STATE
   * ========================================================================== */

  if (!chartData.length) {
    return (
      <div
        className="
          rounded-[18px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <div
          className="
            mb-4
            flex
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[16px]
              font-semibold
              text-[#002B5B]
            "
          >
            Week-to-Date Hours
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-[4px]
              text-[10px]
              text-[#2563EB]
            "
          >
            Daily logged hours vs{' '}
            {targetHours}h target
          </span>
        </div>

        <div
          className="
            flex
            h-[240px]
            items-center
            justify-center
          "
        >
          <span
            className="
              text-[13px]
              text-[#667085]
            "
          >
            No logged hours data
            available
          </span>
        </div>
      </div>
    );
  }

  /* ==========================================================================
   * UI
   * ========================================================================== */

  return (
    <div
      className="
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ==========================================================================
          HEADER
      ========================================================================== */}

      <div
        className="
          mb-4
          flex
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[16px]
            font-semibold
            text-[#002B5B]
          "
        >
          Week-to-Date Hours
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          Daily logged hours vs{' '}
          {targetHours}h target
        </span>
      </div>

      {/* ==========================================================================
          CHART
      ========================================================================== */}

      <ResponsiveContainer
        width="100%"
        height={240}
      >
        <BarChart
          data={chartData}
          margin={{
            top: 10,
            right: 25,
            left: 5,
            bottom: 5,
          }}
          barCategoryGap="25%"
          barGap={2}
        >
          {/* ==========================================================================
              GRID
          ========================================================================== */}

          <CartesianGrid
            vertical={false}
            stroke="#EAEAEA"
            strokeDasharray="2 2"
          />

          {/* ==========================================================================
              X AXIS
          ========================================================================== */}

          <XAxis
            dataKey="day"
            tick={{
              fontSize: 11,
              fill: '#475467',
            }}
            axisLine={false}
            tickLine={false}
            tickMargin={10}
          />

          {/* ==========================================================================
              Y AXIS
          ========================================================================== */}

          <YAxis
            domain={[
              yMin,
              yMax,
            ]}
            ticks={yTicks}
            tickFormatter={(value) =>
              `${value}h`
            }
            tick={{
              fontSize: 11,
              fill: '#475467',
            }}
            width={45}
            axisLine={false}
            tickLine={false}
          />

          {/* ==========================================================================
              TOOLTIP
          ========================================================================== */}

          <Tooltip
            shared={false}
            cursor={{
              fill: '#F8FAFC',
            }}
            content={
              <CustomTooltip />
            }
          />

          {/* ==========================================================================
              LEGEND
          ========================================================================== */}

          <Legend
            verticalAlign="top"
            align="center"
            content={({ payload }) => (
              <div
                className="
                  flex
                  justify-center
                  gap-6
                  pb-3
                  text-[12px]
                "
              >
                {payload?.map(
                  (entry: any) => {
                    const type =
                      entry.dataKey ===
                      'logged'
                        ? 'logged'
                        : 'target';

                    return (
                      <div
                        key={
                          entry.value
                        }
                        className="
                          flex
                          cursor-pointer
                          items-center
                          gap-2
                        "
                        onMouseEnter={() =>
                          setActiveLegend(
                            type,
                          )
                        }
                        onMouseLeave={() =>
                          setActiveLegend(
                            null,
                          )
                        }
                      >
                        <span
                          className="
                            h-3
                            w-3
                          "
                          style={{
                            background:
                              entry.color,

                            opacity:
                              activeLegend &&
                              activeLegend !==
                                type
                                ? 0.3
                                : 1,
                          }}
                        />

                        <span
                          className={
                            activeLegend ===
                            type
                              ? 'font-semibold text-[#101828]'
                              : 'text-[#344054]'
                          }
                        >
                          {entry.value}
                        </span>
                      </div>
                    );
                  },
                )}
              </div>
            )}
          />

          {/* ==========================================================================
              TARGET REFERENCE LINE
          ========================================================================== */}

          <ReferenceLine
            y={targetHours}
            stroke="#EF4444"
            strokeDasharray="4 4"
            label={({
              viewBox,
            }: any) => {
              const {
                x,
                y,
                width,
              } = viewBox;

              const labelWidth =
                62;

              const labelHeight =
                20;

              const labelX =
                x +
                width -
                labelWidth;

              const labelY =
                y - 26;

              return (
                <g>
                  <rect
                    x={labelX}
                    y={labelY}
                    rx={10}
                    ry={10}
                    width={
                      labelWidth
                    }
                    height={
                      labelHeight
                    }
                    fill="#FEE2E2"
                    stroke="#FCA5A5"
                  />

                  <text
                    x={
                      labelX +
                      labelWidth /
                        2
                    }
                    y={
                      labelY + 13
                    }
                    textAnchor="middle"
                    fill="#DC2626"
                    fontSize={11}
                    fontWeight={600}
                  >
                    {targetHours}h
                    target
                  </text>
                </g>
              );
            }}
          />

          {/* ==========================================================================
              TARGET BAR
          ========================================================================== */}

          <Bar
            dataKey="target"
            name="Target"
            fill="#E5E7EB"
            radius={[
              4,
              4,
              0,
              0,
            ]}
            barSize={22}
          >
            {chartData.map(
              (_, index) => (
                <Cell
                  key={`target-${index}`}
                  fill="#E5E7EB"
                  fillOpacity={
                    activeLegend ===
                    'logged'
                      ? 0.25
                      : 1
                  }
                />
              ),
            )}
          </Bar>

          {/* ==========================================================================
              LOGGED BAR
          ========================================================================== */}

          <Bar
            dataKey="logged"
            name="Logged"
            fill="#244E78"
            radius={[
              4,
              4,
              0,
              0,
            ]}
            barSize={22}
          >
            {chartData.map(
              (_, index) => (
                <Cell
                  key={`logged-${index}`}
                  fill="#244E78"
                  fillOpacity={
                    activeLegend ===
                    'target'
                      ? 0.25
                      : 1
                  }
                />
              ),
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeeklyHoursChart;